import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Loader2, Send, BellRing, XCircle, FilePlus2, CheckCircle2 } from "lucide-react";

const QUICK_TYPES = [
    "Certificato di residenza aggiornato",
    "Carta d'identità aggiornata",
    "Tessera sanitaria",
    "Coordinate bancarie (IBAN)",
    "Permesso di soggiorno aggiornato",
];

const STATUS_LABELS = {
    pending: { label: "In attesa", cls: "bg-amber-100 text-amber-800" },
    received: { label: "Ricevuto", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Annullata", cls: "bg-slate-100 text-slate-500" },
};

const fmtDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");

export const DocRequestDialog = ({ open, onClose, employeeId, employeeName }) => {
    const [docType, setDocType] = useState("");
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [requests, setRequests] = useState([]);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/doc-requests", { params: { employee_id: employeeId } });
            setRequests(data || []);
        } catch { /* silent */ }
    }, [employeeId]);

    useEffect(() => {
        if (open) { setDocType(""); setMessage(""); load(); }
    }, [open, load]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (docType.trim().length < 3) { toast.error("Indica il documento richiesto"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/doc-requests", {
                employee_id: employeeId, doc_type: docType.trim(), message: message.trim() || null,
            });
            toast.success(data.emailed
                ? "Richiesta inviata: email recapitata al dipendente"
                : "Richiesta creata (il dipendente la vedrà sul portale; email non inviata)");
            setDocType(""); setMessage("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore creazione richiesta");
        } finally {
            setSaving(false);
        }
    };

    const handleRemind = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/doc-requests/${id}/remind`);
            toast.success("Sollecito inviato");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore invio sollecito");
        } finally { setBusyId(null); }
    };

    const handleCancel = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/doc-requests/${id}/cancel`);
            toast.success("Richiesta annullata");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore annullamento");
        } finally { setBusyId(null); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="doc-request-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <FilePlus2 className="h-5 w-5 text-[#376DB3]" /> Richiedi documento
                    </DialogTitle>
                    <DialogDescription>
                        Il dipendente {employeeName} riceve un'email e vede la richiesta sul portale.
                        Se non carica il documento, il sollecito riparte in automatico ogni 3 giorni.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleCreate} className="space-y-3">
                    <div>
                        <Label htmlFor="dr-type">Documento richiesto *</Label>
                        <Input
                            id="dr-type"
                            value={docType}
                            onChange={(e) => setDocType(e.target.value)}
                            placeholder="es. Certificato di residenza aggiornato"
                            className="mt-1"
                            data-testid="doc-request-type-input"
                        />
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {QUICK_TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => setDocType(t)}
                                    className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                                        docType === t
                                            ? "border-[#376DB3] bg-[#376DB3] text-white"
                                            : "border-slate-200 bg-white text-slate-600 hover:border-[#376DB3] hover:text-[#376DB3]"
                                    }`}
                                    data-testid={`doc-request-quick-${t.slice(0, 10)}`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="dr-msg">Messaggio (facoltativo)</Label>
                        <Textarea
                            id="dr-msg"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="es. Ci risulta un cambio di residenza: invia il certificato aggiornato entro fine mese."
                            rows={2}
                            className="mt-1"
                            data-testid="doc-request-message-input"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={saving}
                        className="w-full gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]"
                        data-testid="doc-request-submit"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Invia richiesta
                    </Button>
                </form>

                {requests.length > 0 && (
                    <div className="mt-2 border-t border-slate-100 pt-3">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Richieste per questo dipendente
                        </div>
                        <ul className="space-y-2">
                            {requests.map((r) => {
                                const st = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
                                return (
                                    <li
                                        key={r.id}
                                        className="flex items-start justify-between gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-2.5"
                                        data-testid={`doc-request-row-${r.id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="text-sm font-medium text-slate-800">{r.doc_type}</span>
                                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                                                    {r.status === "received" && <CheckCircle2 className="mr-0.5 inline h-3 w-3" />}
                                                    {st.label}
                                                </span>
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-slate-500">
                                                Richiesto il {fmtDate(r.created_at)}
                                                {r.reminder_count > 0 && ` · ${r.reminder_count} ${r.reminder_count === 1 ? "sollecito" : "solleciti"}`}
                                                {r.status === "received" && r.fulfilled_at && ` · ricevuto il ${fmtDate(r.fulfilled_at)}`}
                                            </div>
                                        </div>
                                        {r.status === "pending" && (
                                            <div className="flex flex-shrink-0 gap-1">
                                                <Button
                                                    size="sm" variant="outline"
                                                    onClick={() => handleRemind(r.id)}
                                                    disabled={busyId === r.id}
                                                    className="h-7 gap-1 px-2 text-[11px]"
                                                    title="Invia subito un sollecito email"
                                                    data-testid={`doc-request-remind-${r.id}`}
                                                >
                                                    <BellRing className="h-3 w-3" /> Sollecita
                                                </Button>
                                                <Button
                                                    size="sm" variant="ghost"
                                                    onClick={() => handleCancel(r.id)}
                                                    disabled={busyId === r.id}
                                                    className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50"
                                                    title="Annulla la richiesta"
                                                    data-testid={`doc-request-cancel-${r.id}`}
                                                >
                                                    <XCircle className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
